import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { NewSeasonLogo } from '@sorare/core/src/components/Season2027/NewSeasonLogo';
import { LeagueAvatar } from '@sorare/core/src/components/league/LeagueAvatar';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { thickMaterial } from '@sorare/core/src/style/utils';
import { ShopAllCards } from '@sorare/marketplace/src/components/new-season/ShopAllCards';
import type { LeagueLaunch } from '@sorare/marketplace/src/lib/newSeason';
import {
  getLeagueAvatar,
  getLeagueBackgroundUrl,
} from '@sorare/marketplace/src/lib/newSeason';

import { LeagueRewards } from '#src/components/new-season/LeagueRewards/index.tsx';

import { CardsSearch } from './CardsSearch';
import type {
  FeaturedMainLeagueLaunch_competition,
  FeaturedMainLeagueLaunch_so5Competition,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ center: true, gap: 0 })`
  --bg-url: var(--c-nd-50);
  margin: 0 calc(var(--container-padding) * -1);
  padding: var(--quadruple-unit) var(--container-padding) 0;
  background:
    linear-gradient(
      180deg,
      transparent 0%,
      rgba(var(--c-rgb-black), 0.8) calc(100% - 100px),
      rgba(var(--c-rgb-black), 1) 100%
    ),
    var(--bg-url) center / cover no-repeat;

  @container ${tabletAndAbove} {
    padding: calc(8 * var(--unit)) var(--container-padding) 0;
    justify-content: flex-start;
  }
`;

const Hero = styled(Horizontal).attrs({ center: true })`
  padding-bottom: var(--double-unit);
  width: 100%;
  @container ${tabletAndAbove} {
    padding-bottom: var(--quadruple-unit);
  }
`;

const StyledLeagueAvatar = styled(LeagueAvatar)`
  height: calc(var(--unit) * 16);
  width: 100%;
`;

const Block = styled(Vertical).attrs({ gap: 3 })`
  --container-padding: var(--double-unit);
  border: 1px solid var(--c-nd-100);
  border-radius: var(--triple-unit);
  padding: var(--container-padding);
  width: 100%;
  overflow: hidden;
  ${thickMaterial}
`;

const Infos = styled(Vertical).attrs({ center: true, gap: 3 })`
  width: 100%;
  max-width: calc(480px + var(--quadruple-unit));
  padding: 0 var(--quadruple-unit);
  z-index: 1;
`;

const TabletAndAboveContainer = styled.div`
  display: none;
  @container ${tabletAndAbove} {
    display: block;
    width: 100%;
  }
`;

const MobileContainer = styled.div`
  display: block;
  width: 100%;
  @container ${tabletAndAbove} {
    display: none;
  }
`;

const Blocks = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
`;

type Props = {
  league: LeagueLaunch<
    FeaturedMainLeagueLaunch_competition | undefined,
    FeaturedMainLeagueLaunch_so5Competition | undefined
  >;
};

export const FeaturedMainLeagueLaunch = ({ league }: Props) => {
  const backgroundUrl = getLeagueBackgroundUrl(league);
  const leagueAvatar = getLeagueAvatar(league);

  const prizepool = <LeagueRewards league={league} />;

  const leagueNode =
    (!!leagueAvatar?.avatarUrl && (
      <StyledLeagueAvatar league={leagueAvatar} />
    )) ||
    league?.iconNode;

  return (
    <Root
      style={
        {
          '--bg-url': `url(${backgroundUrl})`,
        } as CSSProperties
      }
    >
      <Hero>
        <Infos>
          <NewSeasonLogo size="small" />
          {leagueNode}
          <MobileContainer>{prizepool}</MobileContainer>
          <ShopAllCards size="large" color="secondary" league={league} />
          <TabletAndAboveContainer>{prizepool}</TabletAndAboveContainer>
        </Infos>
      </Hero>
      <Blocks>
        <Block>
          <CardsSearch league={league} />
        </Block>
      </Blocks>
    </Root>
  );
};

FeaturedMainLeagueLaunch.fragments = {
  competition: gql`
    fragment FeaturedMainLeagueLaunch_competition on Competition {
      slug
      ...FeaturedMainLeagueLaunchCardsSearch_competition
      ...ShopAllCards_competition
      ...getLeagueAvatar_competition
      ...getLeagueBackgroundUrl_competition
    }
    ${CardsSearch.fragments.competition}
    ${ShopAllCards.fragments.competition}
    ${getLeagueAvatar.fragments.competition}
    ${getLeagueBackgroundUrl.fragments.competition}
  ` as TypedDocumentNode<FeaturedMainLeagueLaunch_competition>,
  so5Competition: gql`
    fragment FeaturedMainLeagueLaunch_so5Competition on So5Competition {
      slug
      displayName
      ...getLeagueAvatar_so5Competition
      ...getLeagueBackgroundUrl_so5Competition
    }
    ${getLeagueAvatar.fragments.so5Competition}
    ${getLeagueBackgroundUrl.fragments.so5Competition}
  ` as TypedDocumentNode<FeaturedMainLeagueLaunch_so5Competition>,
};
